import React from 'react'

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — `zE` is the global injected by the Zendesk Web Widget script.
const getZendesk = (): undefined | ((...args: unknown[]) => void) => globalThis?.zE

/**
 * Push the user's name/email to the Zendesk Web Widget.
 *
 *   - `webWidget:identify` — the messaging/chat-side identity (used by Zendesk
 *     to attribute conversations and avoid asking the user who they are).
 *   - `webWidget:prefill`  — pre-fills the visible Contact-form `name` /
 *     `email` fields so the user doesn't have to retype them.
 *
 * Both APIs are queued by the Zendesk script if the widget hasn't finished
 * booting yet, so calling early is safe. The previous code called
 * `zE.identify(...)` which silently no-op'd because `zE` is a function (not
 * an object with an `identify` method) — that's why logged-in users saw the
 * Contact form's name/email fields empty.
 */
const pushIdentity = (name?: string, email?: string): void => {
  const zE = getZendesk()
  if (typeof zE !== 'function') return
  if (!name && !email) return

  zE('webWidget', 'identify', { name, email })
  zE('webWidget', 'prefill', {
    name: name ? { value: name } : undefined,
    email: email ? { value: email } : undefined,
  })
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export function useZendesk({ user, loggedIn } = {}) {
  const [wasSet, setWasSet] = React.useState(false)

  const toggleShow = React.useCallback(() => {
    getZendesk()?.('webWidget', 'toggle')

    if (loggedIn) {
      pushIdentity(user?.profile?.name, user?.profile?.email)
    }
  }, [loggedIn, user?.profile?.email, user?.profile?.name])

  const show = React.useCallback(() => {
    getZendesk()?.('webWidget', 'show')
  }, [])

  const hide = React.useCallback(() => {
    getZendesk()?.('webWidget', 'hide')
  }, [])

  const initialize = React.useCallback(() => {
    if (!loggedIn) return
    pushIdentity(user?.profile?.name, user?.profile?.email)
    setWasSet(true)
  }, [loggedIn, user?._id, user?.profile?.email, user?.profile?.name])

  return { toggleShow, show, hide, initialize, wasSet }
}
